import type { FC, ReactNode } from 'react'
import type { MinesState } from '@/types'

import { useEffect, useState } from 'react'
import { Shuffle } from 'lucide-react'
import { api, errorMessage } from '@/lib/client'
import { MINES_DEFAULT, MINES_MAX, MINES_MIN, MINES_TILES, minesMultiplier, payoutFor } from '@/lib/games'
import { formatMultiplier, formatShort } from '@/lib/money'
import { play } from '@/lib/sounds'
import { cn } from '@/lib/utils'
import { useSession } from '@/hooks/use-session'
import { Button } from '@/components/generals/button'
import { inputClass } from '@/components/generals/fields'
import { Arena, BetInput, GameLayout, Panel, StatBox, useStake } from '@/components/games/game-ui'
import { usePlay } from '@/components/games/use-play'
import { PixelSprite } from '@/components/pixel/pixel-sprite'
import { DIAMOND, TNT } from '@/components/pixel/sprites'

type MinesResponse = {
    state: MinesState
    balance: number | null
}

const TILES = Array.from({ length: MINES_TILES }, (_, i) => i)
const MINE_OPTIONS = Array.from({ length: MINES_MAX - MINES_MIN + 1 }, (_, i) => i + MINES_MIN)

const MinesGame: FC = (): ReactNode => {
    const { user, maintenance, setBalance } = useSession()
    const { canBet, toast } = usePlay()
    const stake = useStake()
    const [mines, setMines] = useState(MINES_DEFAULT)
    const [game, setGame] = useState<MinesState | null>(null)
    const [busy, setBusy] = useState<'start' | 'cashout' | number | null>(null)
    const [shake, setShake] = useState(0)

    const active = game?.status === 'active'
    const revealed = game?.revealed ?? []
    const bombCount = game?.mines ?? mines
    const current = active ? minesMultiplier(bombCount, revealed.length) : 1
    const next = minesMultiplier(bombCount, (active ? revealed.length : 0) + 1)
    const canCashOut = active && revealed.length > 0

    // Resume a board that was still open before a refresh.
    const userId = user?.id

    useEffect(() => {
        if (!userId) return

        let cancelled = false

        api<{ state: MinesState | null }>('/api/games/mines')
            .then((data) => {
                if (!cancelled && data.state) {
                    setGame(data.state)
                    setMines(data.state.mines)
                }
            })
            .catch(() => undefined)

        return () => {
            cancelled = true
        }
    }, [userId])

    const apply = (data: MinesResponse, previous: MinesState | null) => {
        setGame(data.state)

        if (data.balance !== null) setBalance(data.balance)

        if (data.state.status === 'lost' && previous?.status === 'active') {
            setShake((value) => value + 1)
            play('boom')
        }
        else if (data.state.status === 'won' && previous?.status === 'active') {
            play('cashout')
        }
        else if (data.state.revealed.length > (previous?.revealed.length ?? 0)) {
            play('gem')
        }
    }

    const start = async () => {
        if (busy !== null || active || !canBet(stake.amount)) return

        setBusy('start')

        try {
            const data = await api<MinesResponse>('/api/games/mines', { body: { action: 'start', amount: stake.amount, mines } })

            setGame(data.state)

            if (data.balance !== null) setBalance(data.balance)

            play('click')
        }
        catch (caught) {
            toast(errorMessage(caught), 'error')
        }
        finally {
            setBusy(null)
        }
    }

    const reveal = async (tile: number) => {
        if (!game || !active || busy !== null || revealed.includes(tile)) return

        setBusy(tile)

        try {
            const data = await api<MinesResponse>('/api/games/mines', { body: { action: 'reveal', betId: game.id, tile } })

            apply(data, game)
        }
        catch (caught) {
            toast(errorMessage(caught), 'error')
        }
        finally {
            setBusy(null)
        }
    }

    const randomTile = () => {
        const hidden = TILES.filter((tile) => !revealed.includes(tile))

        if (hidden.length) void reveal(hidden[Math.floor(Math.random() * hidden.length)])
    }

    const cashOut = async () => {
        if (!game || !canCashOut || busy !== null) return

        setBusy('cashout')

        try {
            const data = await api<MinesResponse>('/api/games/mines', { body: { action: 'cashout', betId: game.id } })

            apply(data, game)
        }
        catch (caught) {
            toast(errorMessage(caught), 'error')
        }
        finally {
            setBusy(null)
        }
    }

    let status: ReactNode = 'Start a game, then pick tiles'

    if (active) status = revealed.length ? `${revealed.length} diamond${revealed.length === 1 ? '' : 's'} found. Keep going or cash out` : `Pick a tile. ${bombCount} TNT hidden`
    if (game?.status === 'won') status = <span className='text-success'>Cashed out at {formatMultiplier(game.multiplier)}: you won {formatShort(game.payout)}</span>
    if (game?.status === 'lost') status = <span className='text-danger'>Boom. You hit TNT</span>

    const panel = (
        <Panel>
            <BetInput value={stake.text} onChange={stake.setText} disabled={active || busy !== null} />

            <div>
                <label htmlFor='mines-count' className='mb-1.5 block text-sm font-semibold text-foreground/90'>
                    TNT on the board
                </label>

                <select
                    id='mines-count'
                    value={mines}
                    onChange={(event) => setMines(Number(event.target.value))}
                    disabled={active || busy !== null}
                    className={cn(inputClass, 'h-12 cursor-pointer')}
                >
                    {MINE_OPTIONS.map((count) => (
                        <option key={count} value={count}>
                            {count} TNT
                        </option>
                    ))}
                </select>
            </div>

            <div className='grid grid-cols-2 gap-2'>
                <StatBox label='Current' value={formatMultiplier(current)} tone={canCashOut ? 'good' : undefined} />
                <StatBox label='Next tile' value={revealed.length + bombCount >= MINES_TILES && active ? '–' : formatMultiplier(next)} />
            </div>

            {active ? (
                <div className='space-y-2'>
                    <Button variant='success' size='lg' className='w-full' onClick={cashOut} loading={busy === 'cashout'} disabled={!canCashOut || (busy !== null && busy !== 'cashout')}>
                        {canCashOut ? `Cash out ${formatShort(payoutFor(game?.amount ?? 0, current))}` : 'Pick a tile first'}
                    </Button>

                    <Button variant='dark' className='w-full' onClick={randomTile} disabled={busy !== null}>
                        <Shuffle className='size-4' aria-hidden='true' />
                        Random tile
                    </Button>
                </div>
            ) : (
                <Button size='lg' className='w-full' onClick={start} loading={busy === 'start'} disabled={maintenance}>
                    {maintenance ? 'Paused for maintenance' : 'Start game'}
                </Button>
            )}
        </Panel>
    )

    const arena = (
        <Arena className='flex flex-col items-center justify-center gap-6 px-4 py-6 sm:px-6'>
            <div key={shake} className={cn('grid w-full max-w-[470px] grid-cols-5 gap-2 sm:gap-2.5', shake > 0 && game?.status === 'lost' && 'df-shake')}>
                {TILES.map((tile) => {
                    const isRevealed = revealed.includes(tile)
                    const isMine = game?.board?.includes(tile) ?? false
                    const finished = game !== null && !active
                    const isHit = game?.hit === tile
                    const open = isRevealed || (finished && game?.board !== null)
                    const label = open ? (isMine ? 'TNT' : 'Diamond') : `Tile ${tile + 1}`

                    return (
                        <button
                            key={tile}
                            type='button'
                            onClick={() => void reveal(tile)}
                            disabled={!active || isRevealed || busy !== null}
                            aria-label={label}
                            className={cn(
                                'relative flex aspect-square items-center justify-center rounded-lg transition duration-150',
                                open ? 'df-tile-open' : 'df-tile',
                                !open && active && busy === null && 'cursor-pointer hover:-translate-y-0.5 hover:brightness-115',
                                !open && !active && 'cursor-default',
                                isHit && 'bg-[#5a1712] shadow-[inset_0_0_0_2px_#ff5a4a]',
                                busy === tile && 'animate-pulse'
                            )}
                        >
                            {open && (
                                <PixelSprite
                                    sprite={isMine ? TNT : DIAMOND}
                                    className={cn(
                                        'w-[58%]',
                                        isRevealed || isHit ? 'df-pop' : 'opacity-35'
                                    )}
                                />
                            )}
                        </button>
                    )
                })}
            </div>

            <p className='text-center text-lg font-bold sm:text-xl' aria-live='polite'>
                {status}
            </p>
        </Arena>
    )

    return <GameLayout panel={panel} arena={arena} />
}

export default MinesGame
